import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  User,
  Award,
  BookOpen,
  MessageSquare,
  Sparkles,
  Send,
  HelpCircle,
  FileText,
  Building2,
  Calendar,
  Check,
  Star,
} from "lucide-react";
import httpService from "../../../services/httpService";

interface CriteriaConfig {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const CRITERIA_LIST: CriteriaConfig[] = [
  {
    key: "communication",
    label: "Communication Skills",
    description:
      "Clarity of thought, articulation, active listening, and effectiveness in conveying technical ideas.",
    icon: <MessageSquare size={20} style={{ color: "#16a34a" }} />,
  },
  {
    key: "skills",
    label: "Technical & Core Skills",
    description:
      "Proficiency in core technologies, hands-on execution ability, framework knowledge, and coding standards.",
    icon: <Award size={20} style={{ color: "#16a34a" }} />,
  },
  {
    key: "module_knowledge",
    label: "Module / Domain Knowledge",
    description:
      "Deep understanding of domain architecture, business logic, design patterns, and system components.",
    icon: <BookOpen size={20} style={{ color: "#16a34a" }} />,
  },
  {
    key: "problem_solving",
    label: "Problem Solving & Analytical Ability",
    description:
      "Analytical reasoning, algorithm design, ability to break down complex constraints, and edge case handling.",
    icon: <Sparkles size={20} style={{ color: "#16a34a" }} />,
  },
  {
    key: "professionalism",
    label: "Overall Professionalism & Soft Skills",
    description:
      "Punctuality, interview preparation, cultural alignment, professional demeanor, and collaboration potential.",
    icon: <User size={20} style={{ color: "#16a34a" }} />,
  },
];

const getScoreBadge = (val: number) => {
  if (val >= 75) {
    return {
      text: "Excellent",
      bg: "#dcfce7",
      color: "#15803d",
      border: "#bbf7d0",
      gradient: "linear-gradient(90deg, #16a34a, #22c55e)",
    };
  } else if (val >= 50) {
    return {
      text: "Good",
      bg: "#fef9c3",
      color: "#a16207",
      border: "#fef08a",
      gradient: "linear-gradient(90deg, #eab308, #ca8a04)",
    };
  } else {
    return {
      text: "Needs Improvement",
      bg: "#fee2e2",
      color: "#b91c1c",
      border: "#fca5a5",
      gradient: "linear-gradient(90deg, #ef4444, #dc2626)",
    };
  }
};

const ExpertFeedbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const scheduleId = searchParams.get("schedule_id") || "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleDetails, setScheduleDetails] = useState<any>(null);

  const [scores, setScores] = useState<{ [key: string]: number }>({
    communication: 80,
    skills: 80,
    module_knowledge: 80,
    problem_solving: 80,
    professionalism: 80,
  });

  const [comments, setComments] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [resultData, setResultData] = useState<any>(null);

  useEffect(() => {
    if (!token || !scheduleId) {
      setError("Invalid link. Missing feedback access token or schedule ID.");
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        setLoading(true);
        const res: any = await httpService.get(
          `/expert/feedback-details?token=${encodeURIComponent(token)}&schedule_id=${encodeURIComponent(scheduleId)}`,
        );
        const data = res?.data || res;
        if (data && (data.status === "success" || data.schedule_id)) {
          setScheduleDetails(data);
          if (data.existing_feedback && data.existing_feedback.scores) {
            setScores(data.existing_feedback.scores);
            setComments(data.existing_feedback.comments || "");
          }
        } else {
          setError(data?.message || "Failed to load interview details.");
        }
      } catch (err: any) {
        console.error("Error fetching feedback details:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Link is invalid or has expired.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [token, scheduleId]);

  const handleScoreChange = (key: string, value: number) => {
    const clamped = Math.min(100, Math.max(0, isNaN(value) ? 0 : value));
    setScores((prev) => ({ ...prev, [key]: clamped }));
  };

  const handleQuickAdd = (word: string) => {
    setComments((prev) => {
      const trimmed = prev.trim();
      if (!trimmed) {
        return word;
      }
      return `${trimmed}\n${word}`;
    });
  };

  const calculatedAverage = React.useMemo(() => {
    const vals = Object.values(scores);
    if (!vals.length) return 0;
    const sum = vals.reduce((acc, curr) => acc + curr, 0);
    return Math.round((sum / vals.length) * 100) / 100;
  }, [scores]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !scheduleId) return;

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        token,
        schedule_id: scheduleId,
        scores,
        comments,
      };

      const res: any = await httpService.post("/expert/feedback", payload);
      const data = res?.data || res;
      if (data && (data.status === "success" || data.schedule_id)) {
        setResultData(data);
        setSubmitted(true);
      } else {
        setError(
          data?.message || "Failed to submit feedback. Please try again.",
        );
      }
    } catch (err: any) {
      console.error("Submission error:", err);
      setError(
        err.response?.data?.message || err.message || "Submission failed.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f0faf4",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "54px",
              height: "54px",
              border: "4px solid #bbf7d0",
              borderTopColor: "#16a34a",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <h3
            style={{
              color: "#14532d",
              fontSize: "18px",
              fontWeight: 700,
              margin: "0 0 6px 0",
            }}
          >
            Loading Evaluation Form
          </h3>
          <p style={{ color: "#166534", fontSize: "14px", margin: 0 }}>
            Validating session details & authentication token...
          </p>
        </div>
      </div>
    );
  }

  if (error && !scheduleDetails) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          style={{
            maxWidth: "520px",
            width: "100%",
            backgroundColor: "#ffffff",
            borderRadius: "20px",
            padding: "36px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.06)",
            textAlign: "center",
            border: "1px solid #fee2e2",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              backgroundColor: "#fee2e2",
              color: "#dc2626",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <AlertCircle size={32} />
          </div>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 800,
              color: "#991b1b",
              marginBottom: "8px",
            }}
          >
            Access Unavailable
          </h2>
          <p
            style={{
              color: "#4b5563",
              fontSize: "14px",
              lineHeight: 1.6,
              marginBottom: "28px",
            }}
          >
            {error}
          </p>
          <button
            onClick={() => navigate("/")}
            style={{
              backgroundColor: "#16a34a",
              color: "#ffffff",
              border: "none",
              padding: "12px 28px",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#f0faf4",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          style={{
            maxWidth: "600px",
            width: "100%",
            backgroundColor: "#ffffff",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(0,0,0,0.08)",
            border: "1px solid #bbf7d0",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #14532d 0%, #16a34a 100%)",
              padding: "44px 32px",
              color: "#ffffff",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
                backdropFilter: "blur(8px)",
              }}
            >
              <CheckCircle2 size={48} style={{ color: "#ffffff" }} />
            </div>
            <h2
              style={{
                fontSize: "26px",
                fontWeight: 800,
                margin: "0 0 10px 0",
                color: "#ffffff",
              }}
            >
              Evaluation Submitted Successfully!
            </h2>
            <p
              style={{
                margin: 0,
                opacity: 0.9,
                fontSize: "15px",
                lineHeight: 1.6,
              }}
            >
              Thank you for providing your expert evaluation for{" "}
              <strong>{scheduleDetails?.session_name}</strong>.
            </p>
          </div>

          <div style={{ padding: "36px", textAlign: "center" }}>
            <div
              style={{
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "16px",
                padding: "24px",
                marginBottom: "28px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#166534",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  marginBottom: "6px",
                }}
              >
                Overall Candidate Score
              </div>
              <div
                style={{
                  fontSize: "42px",
                  fontWeight: 900,
                  color: "#15803d",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {resultData?.interview_expert_score ?? calculatedAverage}
                <span
                  style={{
                    fontSize: "20px",
                    color: "#65a30d",
                    fontWeight: 700,
                  }}
                >
                  / 100
                </span>
              </div>
              {/* <p style={{ margin: '12px 0 0 0', fontSize: '13px', color: '#4b5563', lineHeight: 1.5 }}>
                Your scores have been compiled into the candidate assessment report in the platform database.
              </p> */}
            </div>

            <p style={{ color: "#94a3b8", fontSize: "13px", margin: 0 }}>
              You may safely close this tab now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        paddingBottom: "80px",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Header Bar */}
      <header
        style={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
          padding: "16px 36px",
          position: "sticky",
          top: 0,
          zIndex: 50,
          boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
        }}
      >
        <div
          style={{
            maxWidth: "1080px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <img
              src="https://jozuna-src-images-host.s3.ap-south-1.amazonaws.com/joz+logo.png"
              alt="Joz HRM Logo"
              style={{ width: "42px", height: "42px", objectFit: "contain" }}
            />
            <div>
              <div
                style={{
                  fontSize: "19px",
                  fontWeight: 800,
                  color: "#14532d",
                  lineHeight: 1,
                }}
              >
                Joz HRM
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#16a34a",
                  fontWeight: 600,
                  marginTop: "2px",
                }}
              >
                Powered by JOZUNA
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              padding: "6px 14px",
              borderRadius: "20px",
              fontSize: "13px",
              color: "#15803d",
              fontWeight: 700,
            }}
          >
            <FileText size={16} /> Expert Feedback Portal
          </div>
        </div>
      </header>

      {/* Main Form Content */}
      <main
        style={{ maxWidth: "1080px", margin: "36px auto 0", padding: "0 20px" }}
      >
        {error && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fca5a5",
              color: "#991b1b",
              padding: "16px 24px",
              borderRadius: "14px",
              marginBottom: "28px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontSize: "14px",
            }}
          >
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Candidate & Interview Details Card (Matching Results card aesthetic) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 6px 30px rgba(0,0,0,0.06)",
            border: "1px solid #e2e8f0",
            marginBottom: "36px",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #15803d 0%, #0d9488 100%)",
              color: "#ffffff",
              padding: "32px 36px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <span
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: 800,
                  padding: "5px 14px",
                  borderRadius: "20px",
                  border: "1px solid rgba(255, 255, 255, 0.35)",
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                }}
              >
                Expert Driven Interview Evaluation
              </span>
              <span
                style={{
                  fontSize: "13px",
                  opacity: 0.9,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "rgba(0,0,0,0.15)",
                  padding: "4px 12px",
                  borderRadius: "12px",
                }}
              >
                <Clock size={15} /> {scheduleDetails?.start_datetime}
              </span>
            </div>

            <h1
              style={{
                fontSize: "26px",
                fontWeight: 800,
                margin: "0 0 18px 0",
                color: "#ffffff",
              }}
            >
              {scheduleDetails?.session_name}
            </h1>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "24px",
                fontSize: "14px",
                color: "#e2e8f0",
              }}
            >
              <div>
                <span
                  style={{
                    opacity: 0.8,
                    fontSize: "11px",
                    display: "block",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "3px",
                  }}
                >
                  Candidate(s)
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    color: "#ffffff",
                    fontSize: "15px",
                  }}
                >
                  {Array.isArray(scheduleDetails?.candidates)
                    ? scheduleDetails.candidates.join(", ")
                    : scheduleDetails?.candidates || "N/A"}
                </span>
              </div>
              <div>
                <span
                  style={{
                    opacity: 0.8,
                    fontSize: "11px",
                    display: "block",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "3px",
                  }}
                >
                  Interviewer Email
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    color: "#ffffff",
                    fontSize: "15px",
                  }}
                >
                  {scheduleDetails?.interviewer_email}
                </span>
              </div>
              <div>
                <span
                  style={{
                    opacity: 0.8,
                    fontSize: "11px",
                    display: "block",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: "3px",
                  }}
                >
                  Round & Duration
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    color: "#ffffff",
                    fontSize: "15px",
                  }}
                >
                  {scheduleDetails?.round_type || "Technical"} (
                  {scheduleDetails?.duration} mins)
                </span>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "16px 36px",
              backgroundColor: "#f0fdf4",
              borderTop: "1px solid #bbf7d0",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "14px",
              color: "#14532d",
              fontWeight: 600,
            }}
          >
            <HelpCircle size={20} className="text-emerald-700" />
            <span>
              Please complete the feedback for the candidate across the 5
              standard categories below (0 - 100 max score each).
            </span>
          </div>
        </div>

        {/* 5 Feedback Form Categories */}
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "28px",
              marginBottom: "36px",
            }}
          >
            {CRITERIA_LIST.map((item, idx) => {
              const currentScore = scores[item.key] ?? 80;
              const badge = getScoreBadge(currentScore);

              return (
                <div
                  key={item.key}
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "20px",
                    padding: "28px 32px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                    border: "1px solid #e2e8f0",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "20px",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#f0fdf4",
                          padding: "12px",
                          borderRadius: "14px",
                          border: "1px solid #dcfce7",
                        }}
                      >
                        {item.icon}
                      </div>
                      <div>
                        <h3
                          style={{
                            fontSize: "17px",
                            fontWeight: 800,
                            margin: "0 0 4px 0",
                            color: "#0f172a",
                          }}
                        >
                          {idx + 1}. {item.label}
                        </h3>
                        <p
                          style={{
                            fontSize: "13px",
                            color: "#64748b",
                            margin: 0,
                            lineHeight: 1.5,
                          }}
                        >
                          {item.description}
                        </p>
                      </div>
                    </div>

                    <span
                      style={{
                        backgroundColor: badge.bg,
                        color: badge.color,
                        border: `1px solid ${badge.border}`,
                        fontSize: "12px",
                        fontWeight: 800,
                        padding: "6px 14px",
                        borderRadius: "20px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {badge.text}
                    </span>
                  </div>

                  {/* Dual Interactive Controls (Slider + Numeric Input) */}
                  <div
                    style={{
                      backgroundColor: "#f8fafc",
                      borderRadius: "16px",
                      padding: "20px 24px",
                      border: "1px solid #f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      gap: "24px",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: "240px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "11px",
                          color: "#94a3b8",
                          fontWeight: 700,
                          marginBottom: "8px",
                        }}
                      >
                        <span>0 (Poor)</span>
                        <span>50 (Average)</span>
                        <span>100 (Exceptional)</span>
                      </div>

                      <div style={{ position: "relative" }}>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={currentScore}
                          onChange={(e) =>
                            handleScoreChange(item.key, Number(e.target.value))
                          }
                          style={{
                            width: "100%",
                            height: "10px",
                            accentColor: "#16a34a",
                            borderRadius: "5px",
                            cursor: "pointer",
                            backgroundColor: "#e2e8f0",
                            outline: "none",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={currentScore}
                        onChange={(e) =>
                          handleScoreChange(item.key, Number(e.target.value))
                        }
                        style={{
                          width: "80px",
                          padding: "10px 14px",
                          fontSize: "18px",
                          fontWeight: 800,
                          color: "#15803d",
                          backgroundColor: "#ffffff",
                          border: "2px solid #bbf7d0",
                          borderRadius: "12px",
                          textAlign: "center",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                          outline: "none",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "15px",
                          fontWeight: 700,
                          color: "#64748b",
                        }}
                      >
                        / 100
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Qualitative Comments Block */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              padding: "28px 32px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
              border: "1px solid #e2e8f0",
              marginBottom: "36px",
            }}
          >
            <h3
              style={{
                fontSize: "17px",
                fontWeight: 800,
                margin: "0 0 6px 0",
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <MessageSquare size={20} style={{ color: "#16a34a" }} />{" "}
              Additional Comments & Interviewer Remarks (Optional)
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "#64748b",
                margin: "0 0 16px 0",
              }}
            >
              Add specific observations regarding candidate strengths,
              weaknesses, or hiring recommendations.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Quick Select:</span>
              {[
                { label: 'Hired', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                { label: 'Not Hired', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
                { label: 'On Hold', color: '#d97706', bg: '#fffbeb', border: '#fef08a' }
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleQuickAdd(item.label)}
                  style={{
                    backgroundColor: item.bg,
                    border: `1px solid ${item.border}`,
                    borderRadius: '20px',
                    padding: '6px 14px',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: item.color,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in-out',
                    outline: 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
                  }}
                >
                  + {item.label}
                </button>
              ))}
            </div>
            <textarea
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="e.g. Candidate demonstrated strong problem solving capabilities in database design, but could improve code structuring..."
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #cbd5e1",
                fontSize: "14px",
                lineHeight: 1.6,
                color: "#1e293b",
                outline: "none",
                resize: "vertical",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
          </div>

          {/* Bottom Action Bar & Score Preview */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "24px",
              padding: "28px 36px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
              border: "1px solid #bbf7d0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "24px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  marginBottom: "4px",
                }}
              >
                Overall Calculated Evaluation Score
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: 900,
                  color: "#15803d",
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8px",
                }}
              >
                {calculatedAverage}{" "}
                <span
                  style={{
                    fontSize: "18px",
                    color: "#65a30d",
                    fontWeight: 700,
                  }}
                >
                  / 100
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                backgroundColor: submitting ? "#94a3b8" : "#16a34a",
                color: "#ffffff",
                border: "none",
                padding: "16px 40px",
                borderRadius: "14px",
                fontSize: "16px",
                fontWeight: 800,
                cursor: submitting ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                boxShadow: submitting
                  ? "none"
                  : "0 6px 20px rgba(22, 163, 74, 0.35)",
                transition: "all 0.2s ease-in-out",
              }}
            >
              {submitting ? (
                <>
                  <div
                    style={{
                      width: "18px",
                      height: "18px",
                      border: "3px solid #ffffff",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  Submitting Feedback...
                </>
              ) : (
                <>
                  <Send size={20} /> Submit Candidate Feedback
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ExpertFeedbackPage;
